/**
 * Hook for auto enrichment eligibility — batch eligibility check for bulk auto-enrich.
 *
 * Mirrors usePhoneEnrichment but targets ahoy_enrichment_type==='auto' requests
 * and applies the three-bucket eligibility rule:
 *
 *   in_progress       — any auto request with status in ['queued', 'pending']
 *   recently_enriched — most-recent complete auto request within 365 days
 *   eligible          — everything else (no prior, only failed, or stale complete)
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { apiPost } from '@/lib/http/client';
import type { CrmObject } from '@/types/crm';

export interface AutoEnrichmentInfo {
  status: 'idle' | 'in_progress' | 'recently_enriched' | 'eligible';
  isEligible: boolean;
  disabledReason?: string;
}

export interface UseAutoEnrichmentEligibilityResult {
  infoMap: Map<string, AutoEnrichmentInfo>;
  isLoading: boolean;
  isError: boolean;
}

const DAYS_365_MS = 365 * 24 * 60 * 60 * 1000;

interface EnrichmentRequestEntry {
  id: string;
  entity_id: string;
  property_values: {
    ahoy_enrichment_type?: string;
    ahoy_status?: string;
    ahoy_completed_at?: string;
    ahoy_created_at?: string;
  };
}

interface BatchObjectsResponse {
  objects: Record<string, EnrichmentRequestEntry>;
}

// The by-entity-ids endpoint enforces a max of 100 IDs per request. Keep
// chunks comfortably under that so a single bulk-enrich selection (capped at
// BULK_ACTION_ASYNC_THRESHOLD=100 contacts, each potentially with multiple
// enrichment requests) doesn't blow the limit.
const BY_ENTITY_IDS_MAX_PER_REQUEST = 100;

export function useAutoEnrichmentEligibility(
  orgId: number | undefined,
  contacts: CrmObject[]
): UseAutoEnrichmentEligibilityResult {
  // Extract enrichment request IDs from all contacts' associations.
  // Sort the deduped result so the queryKey is order-independent — contacts
  // sometimes arrive in different orders across renders (sorting changes,
  // pagination, store updates) and we don't want that to cause cache misses
  // or refetches when the underlying set of ids is unchanged.
  const enrichmentRequestIds = useMemo(() => {
    const ids: string[] = [];
    for (const contact of contacts) {
      for (const assoc of contact.associations || []) {
        if (
          assoc.association_type_name ===
            'ahoy_contact_to_enrichment_request' &&
          assoc.target_entity_id
        ) {
          ids.push(assoc.target_entity_id);
        }
      }
    }
    return [...new Set(ids)].sort();
  }, [contacts]);

  const contactEntityIds = useMemo(
    () => contacts.map(c => c.entity_id).sort(),
    [contacts]
  );

  const {
    data: enrichmentRequests,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      'auto-enrichment-eligibility',
      orgId,
      contactEntityIds.join(','),
      ...enrichmentRequestIds,
    ],
    queryFn: async () => {
      if (enrichmentRequestIds.length === 0) {
        return {};
      }
      // Chunk into batches of BY_ENTITY_IDS_MAX_PER_REQUEST so we never
      // exceed the by-entity-ids endpoint's 100-ID cap. Each chunk's
      // response is merged into a single map keyed by entity_id.
      const merged: Record<string, EnrichmentRequestEntry> = {};
      for (
        let i = 0;
        i < enrichmentRequestIds.length;
        i += BY_ENTITY_IDS_MAX_PER_REQUEST
      ) {
        const chunk = enrichmentRequestIds.slice(
          i,
          i + BY_ENTITY_IDS_MAX_PER_REQUEST
        );
        const response = await apiPost<BatchObjectsResponse>(
          `/api/orgs/${orgId}/crm-events/crm-objects/by-entity-ids/`,
          // by-entity-ids now defaults include_associations to FALSE. This hook
          // only reads `property_values.ahoy_enrichment_type` off the fetched
          // requests (the association walk above is over the CONTACT's own
          // associations), so this passes false explicitly to guard against a
          // future default flip.
          { entity_ids: chunk, include_associations: false }
        );
        Object.assign(merged, response.objects);
      }
      return merged;
    },
    enabled: !!orgId && contacts.length > 0,
    staleTime: 30 * 1000,
    retry: 2,
  });

  const infoMap = useMemo(() => {
    const map = new Map<string, AutoEnrichmentInfo>();
    const objectsMap = enrichmentRequests ?? {};
    const now = Date.now();
    const staleCutoff = now - DAYS_365_MS;

    for (const contact of contacts) {
      // Collect all auto enrichment requests for this contact
      const autoRequests: EnrichmentRequestEntry[] = [];
      for (const assoc of contact.associations || []) {
        if (
          assoc.association_type_name === 'ahoy_contact_to_enrichment_request'
        ) {
          const req = objectsMap[assoc.target_entity_id];
          if (req?.property_values?.ahoy_enrichment_type === 'auto') {
            autoRequests.push(req);
          }
        }
      }

      let status: AutoEnrichmentInfo['status'] = 'eligible';
      let disabledReason: string | undefined;

      if (autoRequests.length > 0) {
        // In-progress check takes highest priority — work already in flight
        // blocks regardless of any later request's status.
        const hasInProgress = autoRequests.some(
          r =>
            r.property_values.ahoy_status === 'queued' ||
            r.property_values.ahoy_status === 'pending'
        );

        if (hasInProgress) {
          status = 'in_progress';
          disabledReason = 'Enrichment in progress';
        } else {
          // Find the most-recent auto request by ahoy_created_at.
          // Missing created_at is treated as `now` (most-recent) so a fresh
          // request without the timestamp populated still ranks first.
          let mostRecentReq = autoRequests[0];
          let mostRecentReqCreatedMs = (() => {
            const raw = mostRecentReq.property_values.ahoy_created_at;
            const ts = raw ? new Date(raw).getTime() : now;
            return isNaN(ts) ? now : ts;
          })();
          for (const req of autoRequests.slice(1)) {
            const raw = req.property_values.ahoy_created_at;
            const ts = raw ? new Date(raw).getTime() : now;
            const norm = isNaN(ts) ? now : ts;
            if (norm > mostRecentReqCreatedMs) {
              mostRecentReq = req;
              mostRecentReqCreatedMs = norm;
            }
          }

          const mostRecentStatus = mostRecentReq.property_values.ahoy_status;

          if (mostRecentStatus === 'complete') {
            // Treat missing completed_at as now (most-recent), so the request
            // is NOT stale and remains ineligible.
            const rawCompleted =
              mostRecentReq.property_values.ahoy_completed_at;
            const completedMs = rawCompleted
              ? new Date(rawCompleted).getTime()
              : now;
            const completedNorm = isNaN(completedMs) ? now : completedMs;
            if (completedNorm >= staleCutoff) {
              status = 'recently_enriched';
              disabledReason = 'Recently enriched (within 1 year)';
            }
            // else: most-recent complete is stale → eligible
          }
          // else: most-recent is failed (or unknown) → eligible
        }
      }

      map.set(contact.entity_id, {
        status,
        isEligible: status === 'eligible',
        disabledReason,
      });
    }

    return map;
  }, [contacts, enrichmentRequests]);

  return { infoMap, isLoading, isError };
}
